import { Box, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  PortalAwardOutcomeDto,
  type PortalAwardLetterDto,
  type PortalSourcingEventDetailDto,
  useGetPortalAwardLetterQuery,
} from "../../../../autogen/bff-api";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { usePortalProcurementTerms } from "../../../../common/terminology/useProcurementTerms";
import { AwardLetterPreview } from "../../../v2/sourcing-event/award/AwardLetterPreview";
import { winnerNamesText } from "../../../v2/sourcing-event/award/awardLetterTemplates";
import { AwardOutcomeBanner } from "./AwardOutcomeBanner";
import { awardNotificationCriteria, awardNotificationScores } from "./awardNotificationCriteria";
import { isPrivatePortalEvent } from "./PrivateInviteCta";

export const EventAwardNotificationSection = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const { data } = useGetPortalAwardLetterQuery({ eventId: event.id });
  if (!data || (!data.letter && !data.outcome)) return null;
  const withdrawn = data.outcome === PortalAwardOutcomeDto.Withdrawn;

  return (
    <Stack spacing="3">
      <Text fontSize="lg" fontWeight="semibold" color="smText">
        {t("Result")}
      </Text>
      <AwardOutcomeBanner event={event} award={data} />
      {withdrawn ? null : data.letter ? (
        <SentAwardLetter event={event} award={data} />
      ) : (
        data.letterPending && (
          <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
            <Text fontWeight="semibold" color="smText">
              {t("Your award letter is on its way")}
            </Text>
            <Text fontSize="sm" color="smMuted">
              {t("You will be notified by email when the reasons are ready.")}
            </Text>
          </Box>
        )
      )}
    </Stack>
  );
};

const SentAwardLetter = ({ event, award }: { event: PortalSourcingEventDetailDto; award: PortalAwardLetterDto }) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const terms = usePortalProcurementTerms(event);
  const letter = award.letter;
  if (!letter) return null;

  const criteria = awardNotificationCriteria(
    award.criteria ?? [],
    event.awardCriteria?.criteria ?? [],
    t("Criterion") || "",
  );
  const scores = awardNotificationScores(award.scores ?? {});
  const standstillExpiresAt = award.standstillExpiresAt ?? undefined;

  return (
    <Stack spacing="3">
      <Text fontSize="md" fontWeight="semibold" color="smText">
        {t("Your award notification")}
      </Text>
      <AwardLetterPreview
        type={letter.type}
        supplierName={letter.supplierName || ""}
        winnerName={winnerNamesText(award.winnerNames ?? [])}
        eventTitle={event.title}
        senderName={event.buyerName}
        signerName={award.signerName ?? event.buyerContactName}
        scoreHeading={terms.scorePerAwardCriterion}
        criterionJustifications={letter.content.criterionJustifications ?? {}}
        freeText={letter.content.freeText}
        criteria={criteria}
        scores={scores}
        sentAt={letter.sentAt}
        clauseContext={{
          eventRunsPrivateFlow: isPrivatePortalEvent(event),
          buyerRunsPublicProcurements: event.buyerRunsPublicProcurements ?? false,
          regulation: award.regulation,
        }}
      />
      {standstillExpiresAt && (
        <Box bg="smBackgroundSecondary" rounded="md" border="1px solid" borderColor="smBorder" p="4">
          <Text fontSize="sm" color="smText">
            {t("The award decision is not final until {{date}}, during which it may be challenged.", {
              date: displayDate(standstillExpiresAt),
            })}
          </Text>
        </Box>
      )}
    </Stack>
  );
};
